var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { AutoCompleteEnum, ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { InputModeEnum, KeyboardManager } from '@inter-ikea-kompis/keyboard-manager';
import { IdGenerator, SafariWriteDirectionUtility } from '@inter-ikea-kompis/utilities';
import { Icon } from '@inter-ikea-kompis/component-icon';
import { Text } from '@inter-ikea-kompis/component-text';
import InputLayoutEnum from '../../enums/InputLayoutEnum.js';
import InputTypeEnum from '../../enums/InputTypeEnum.js';
import InputStyle from './InputStyle.js';
const isSafari = navigator.userAgent.search('Safari') >= 0 && navigator.userAgent.search('Chrome') < 0;
/**
 * @deprecated This component has been deprecated, see Kompis page for more info.
 *
 * @event input When inputing values
 * @event focusOut When input field is put out of focus
 */
export default class Input extends KompisElement {
    constructor() {
        super(...arguments);
        // Input mode
        this.inputMode = InputModeEnum.text;
        // Layout
        this.layout = InputLayoutEnum.default;
        // Sets the autocomplete behaviour. Note that it should be set to "off" in Kiosks due to privacy policies, as it can otherwise store user data
        this.autocomplete = AutoCompleteEnum.off;
        // Set the value of the field
        this.value = '';
        // Will be focused when created
        this.autofocus = false;
        // Like a native input element
        this.disabled = false;
        // Like a native input element
        this.spellcheck = false;
        // Label
        this.label = null;
        // Help message
        this.helpMessage = null;
        // Prefix.
        this.prefix = null;
        // Suffix.
        this.suffix = null;
        // Character count
        this.characterCount = null;
        // Put an icon in the ending of the input field
        this.trailingIcon = null;
        // Put an icon in the beginning of the input field
        this.leadingIcon = null;
        // SelectionStart of cursor
        this.selectionStart = null;
        // SelectionEnd of cursor
        this.selectionEnd = null;
        // Type of input
        this.type = InputTypeEnum.default;
        // Name of input
        this.inputName = '';
        this.hasFocus = false;
        this.cursor = {
            selectionStart: null,
            selectionEnd: null
        };
        this.listeners = {
            input: {
                handleEvent: this.onInput.bind(this),
                passive: false
            },
            pointerup: this.onSelectionChange.bind(this),
            keyup: this.onSelectionChange.bind(this),
            focusout: this.onFocusOut.bind(this),
            focusin: this.onFocusIn.bind(this)
        };
        this.keyboardManager = new KeyboardManager();
        this.hasBeenAutoFocused = false;
        this.labelId = new IdGenerator().generateId();
        this.errorId = new IdGenerator().generateId();
    }
    /**
     * Renders the component.
     *
     * @returns Template.
     */
    render() {
        const direction = SafariWriteDirectionUtility.getWriteDirection(this);
        return html `
			<style>
				${InputStyle.getStyle({
            hasLabel: this.label ? true : false,
            disabled: this.disabled,
            layout: this.layout,
            direction: direction,
            hasFocus: this.hasFocus,
            hasValue: this.value !== '' && this.value !== null && this.value !== 'null',
            hasLeadingIcon: this.leadingIcon !== null,
            hasTrailingIcon: this.trailingIcon !== null,
            theme: this.theme,
            characterCountExceeded: this.characterCount
                ? this.value.length > this.characterCount
                : false,
            type: this.type
        })}
			</style>
			<div class="container">
				<div class="input">
					<div class="labelContainer">${this.getLabel()}</div>
					${this.getLeadingIcon()} ${this.getPrefix()} ${this.getInputElement()} ${this.getSuffix()}
					${this.getTrailingIcon()}
				</div>
				${this.getHelpContainer()}
			</div>
		`;
    }
    update() {
        super.update();
        // FIXME
        // Temporary fix - Safari does not seem to let go of focus when running the code in the if-statement
        if (isSafari) {
            return;
        }
        const input = this.shadowRoot?.querySelector('input');
        if (input) {
            if (this.autofocus && !this.hasBeenAutoFocused) {
                this.hasBeenAutoFocused = true;
                input.focus();
                if (this.value) {
                    this.cursor.selectionStart = this.cursor.selectionStart
                        ? this.cursor.selectionStart
                        : this.value.length;
                    this.cursor.selectionEnd = this.cursor.selectionEnd
                        ? this.cursor.selectionEnd
                        : this.value.length;
                }
            }
            // Forces update of selectionStart and selectionEnd after render
            // Sometimes it does not seem to want to update it during render
            input.selectionStart =
                this.selectionStart !== null ? this.selectionStart : this.cursor.selectionStart;
            input.selectionEnd =
                this.selectionEnd !== null ? this.selectionEnd : this.cursor.selectionEnd;
        }
    }
    connectedCallback() {
        this.customElements.define('kompis-icon', Icon);
        this.customElements.define('kompis-text', Text);
        super.connectedCallback();
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('autofocus') && !this.autofocus) {
            this.hasBeenAutoFocused = false;
        }
        return true;
    }
    /**
     * @returns Prefix.
     */
    getPrefix() {
        if (!this.prefix) {
            return null;
        }
        return html `
			${this.leadingIcon ? html `<span class="divider"></span>` : null}
			<span
				class="prefix"
				@keydown="${(event) => event.key === 'Enter' && this.onClick()}"
				@click="${() => this.onClick()}"
				><kompis-text .theme="${this.theme}" type="${ThemeFontStyleTypeEnum.bodyLarge}"
					>${this.prefix}</kompis-text
				></span
			>
			<span class="divider"></span>
		`;
    }
    /**
     * @returns Suffix.
     */
    getSuffix() {
        if (!this.suffix) {
            return null;
        }
        return html `
			<span class="divider"></span>
			<span
				class="suffix"
				@keydown="${(event) => event.key === 'Enter' && this.click()}"
				@click="${() => this.onClick()}"
				><kompis-text .theme="${this.theme}" type="${ThemeFontStyleTypeEnum.bodyLarge}"
					>${this.suffix}</kompis-text
				></span
			>
			${this.trailingIcon ? html `<span class="divider"></span>` : null}
		`;
    }
    /**
     * @returns Suffix.
     */
    getCharacterCount() {
        if (!this.characterCount) {
            return null;
        }
        return html `
			<span class="characterCount">
				<kompis-text .type="${ThemeFontStyleTypeEnum.bodySmall}" .theme="${this.theme}">
					${this.value.length} / ${this.characterCount}
				</kompis-text>
			</span>
		`;
    }
    /**
     * @returns Template.
     */
    getHelpContainer() {
        if (!this.helpMessage && !this.characterCount) {
            return null;
        }
        return html `
			<div class="helpContainer">
				${this.getHelpField()}
				<div class="divider"></div>
				${this.getCharacterCount()}
			</div>
		`;
    }
    errorMessage() {
        return html `<kompis-text
			id="${this.errorId}"
			.theme="${this.theme}"
			.type="${ThemeFontStyleTypeEnum.bodySmall}"
			>${this.helpMessage}</kompis-text
		>`;
    }
    infoMessage() {
        return html `<span>
			<kompis-text .theme="${this.theme}" .type="${ThemeFontStyleTypeEnum.bodySmall}"
				>${this.helpMessage}</kompis-text
			>
		</span>`;
    }
    /**
     * @returns Template.
     */
    getHelpField() {
        return html `
			<div class="helpMessage" id="helpMessage">
				<span role="status">
					${this.layout === InputLayoutEnum.error ? this.errorMessage() : null}</span
				>
				${this.layout !== InputLayoutEnum.error ? this.infoMessage() : null}
			</div>
		`;
    }
    /**
     * @returns Template.
     */
    getLeadingIcon() {
        if (!this.leadingIcon) {
            return null;
        }
        return html `
			<kompis-icon .theme="${this.theme}" .icon="${this.leadingIcon}"></kompis-icon>
			<span class="divider"></span>
		`;
    }
    /**
     * @returns Template.
     */
    getTrailingIcon() {
        if (!this.trailingIcon) {
            return null;
        }
        return html `
			<span class="divider"></span>
			<kompis-icon .theme="${this.theme}" .icon="${this.trailingIcon}"></kompis-icon>
		`;
    }
    /**
     * @returns Template.
     */
    getInputElement() {
        return html `
			<input
				.selectionEnd="${this.selectionEnd !== null ? this.selectionEnd : this.cursor.selectionEnd}"
				@pointerup="${this.listeners.pointerup}"
				@keyup="${this.listeners.keyup}"
				@input="${this.listeners.input}"
				@focusin="${this.listeners.focusin}"
				@focusout="${this.listeners.focusout}"
				@blur="${(event) => event.preventDefault()}"
				.value="${this.parseValue(this.value)}"
				.selectionStart="${this.selectionStart !== null
            ? this.selectionStart
            : this.cursor.selectionStart}"
				id="${this.labelId}"
				name="${this.inputName}"
				inputmode="${this.inputMode}"
				autocomplete="${this.autocomplete}"
				?autofocus="${this.autofocus}"
				spellcheck="${this.spellcheck}"
				?disabled="${this.disabled}"
				?readonly="${this.layout === InputLayoutEnum.readOnly}"
				aria-invalid="${this.layout === InputLayoutEnum.error}"
				aria-errormessage="${this.layout === InputLayoutEnum.error ? this.errorId : ''}"
				aria-describedby="helpMessage"
			/>
		`;
    }
    /**
     * @returns Template.
     */
    getLabel() {
        return html ` <label for="${this.labelId}"> ${this.label} </label> `;
    }
    /**
     * HTML event handler.
     */
    onFocusOut() {
        this.keyboardManager.removeFocus();
        this.dispatchEvent(new CustomEvent('focusOut', {
            bubbles: true,
            detail: {
                value: this.value
            }
        }));
        // FIXME
        // Temporary fix - Safari gets really slow when running the code below, so we exclude it for now.
        if (isSafari === false) {
            this.cursor.selectionStart = null;
            this.cursor.selectionEnd = null;
        }
        this.hasFocus = false;
        this.update();
        this.shadowRoot?.querySelector('input')?.blur();
    }
    /**
     * HTML event handler.
     *
     * @param event ...
     */
    onFocusIn(event) {
        const srcElement = event.currentTarget;
        this.cursor.selectionStart = srcElement.selectionStart;
        this.cursor.selectionEnd = srcElement.selectionEnd;
        if (this.layout === InputLayoutEnum.readOnly) {
            return;
        }
        const inputElement = this.shadowRoot?.querySelector('input');
        if (inputElement) {
            this.keyboardManager.setFocus(inputElement);
        }
        this.hasFocus = true;
        this.update();
    }
    /**
     * HTML event handler.
     */
    onClick() {
        this.shadowRoot?.querySelector('input')?.focus();
    }
    /**
     * Runs when input event occurs.
     *
     * @param event HTMLEvent.
     */
    onInput(event) {
        event.stopPropagation();
        event.preventDefault();
        const srcElement = event.currentTarget;
        const newValue = this.parseValue(srcElement.value);
        const eventDetails = {
            data: event.data, // Last character
            value: newValue,
            valueAsNumber: this.stringToNumberValue(newValue),
            selectionStart: srcElement.selectionStart,
            selectionEnd: srcElement.selectionEnd
        };
        this.cursor.selectionStart = srcElement.selectionStart;
        this.cursor.selectionEnd = srcElement.selectionEnd;
        // Reset value to make this input stateless
        srcElement.value = this.value;
        this.dispatchEvent(new CustomEvent('input', { bubbles: true, detail: eventDetails }));
    }
    /**
     * Runs when selection changes.
     *
     * @param event HTMLEvent.
     */
    onSelectionChange(event) {
        this.cursor.selectionStart = event.target.selectionStart;
        this.cursor.selectionEnd = event.target.selectionEnd;
    }
    /**
     * Parses an input value based on the InputModeEnum prop.
     *
     * @param value Value from props.
     * @returns Parsed value.
     */
    parseValue(value) {
        if (!value) {
            return '';
        }
        switch (this.inputMode) {
            case InputModeEnum.tel: {
                return value.replace(/[^-+0-9]/g, '');
            }
            case InputModeEnum.numeric:
                return value.replace(/[^-0-9]/g, '');
            case InputModeEnum.decimal:
                return value.replace(/[^-0-9.,]/g, '');
        }
        return value;
    }
    /**
     * Converts string to number if InputModeEnum is number or decimal.
     *
     * @param value String value.
     * @returns Converted value.
     */
    stringToNumberValue(value) {
        switch (this.inputMode) {
            case InputModeEnum.numeric:
            case InputModeEnum.decimal:
                return parseFloat(value.replace(/,/g, '.'));
        }
        return null;
    }
}
__decorate([
    property({ type: Object, required: true })
], Input.prototype, "theme", void 0);
__decorate([
    property({ type: String })
], Input.prototype, "inputMode", void 0);
__decorate([
    property({ type: String })
], Input.prototype, "layout", void 0);
__decorate([
    property({ type: String })
], Input.prototype, "autocomplete", void 0);
__decorate([
    property({ type: String, synchronous: true })
], Input.prototype, "value", void 0);
__decorate([
    property({ type: Boolean })
    // eslint-disable-next-line lit/no-native-attributes
], Input.prototype, "autofocus", void 0);
__decorate([
    property({ type: Boolean })
], Input.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean })
    // eslint-disable-next-line lit/no-native-attributes
], Input.prototype, "spellcheck", void 0);
__decorate([
    property({ type: String })
], Input.prototype, "label", void 0);
__decorate([
    property({ type: String })
], Input.prototype, "helpMessage", void 0);
__decorate([
    property({ type: String })
], Input.prototype, "prefix", void 0);
__decorate([
    property({ type: String })
], Input.prototype, "suffix", void 0);
__decorate([
    property({ type: Number })
], Input.prototype, "characterCount", void 0);
__decorate([
    property({ type: String })
], Input.prototype, "trailingIcon", void 0);
__decorate([
    property({ type: String })
], Input.prototype, "leadingIcon", void 0);
__decorate([
    property({ type: Number, synchronous: true })
], Input.prototype, "selectionStart", void 0);
__decorate([
    property({ type: Number, synchronous: true })
], Input.prototype, "selectionEnd", void 0);
__decorate([
    property({ type: String })
], Input.prototype, "type", void 0);
__decorate([
    property({ type: String })
], Input.prototype, "inputName", void 0);
//# sourceMappingURL=Input.js.map